import { CreatorBrand } from 'constants/brands';
import React, {
    useCallback,
    useRef,
    useEffect,
    useMemo,
    useState,
    Suspense,
} from 'react';
import {
    Box,
    Collapse,
    Progress,
    Scrollable,
    Text,
    useLockBodyScroll,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import { Hidden } from 'components/CommonUi/Hidden';
import { withErrorBoundary } from 'error';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { lazyWithRetries } from 'utils/lazyWithRetries';
import { CreatorHeader } from '../CreatorHeader/CreatorHeader';
import { creator } from '../Creator.styled';
import CreatorFooter from '../CreatorFooter';
import SamplesCount from '../SamplesCount';

const CreatorSamplesTable = lazyWithRetries(
    () => import('../CreatorSamplesTable'),
);

const MemoizedCreatorSamplesTable = React.memo(CreatorSamplesTable);

const CreatorCollapse = () => {
    // CONTEXT
    const {
        toggleCreator,
        togglePause,
        state,
        dispatch,
        isVisible,
        isInitialized,
    } = useCreatorContext();
    const { isCreatorOpen, samplesInSession, isSessionProcessing } = state;
    const samplesCount = samplesInSession.size;

    const creatorContentRef = useRef<HTMLDivElement>(null);
    const creatorSimpleBarRef = useRef<HTMLDivElement>(null);
    const creatorScrollReflike: { current: null | HTMLElement } = useMemo(
        () => ({
            current: null,
        }),
        [],
    );

    // STATE
    const [useLock, setUseLock] = useState(false);

    // MEMO
    // Since we only need the keys, we can safely only use the sampleCount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const sampleIds = useMemo(() => [...state.samples.keys()], [samplesCount]);

    // HOOKS
    useLockBodyScroll({
        initialLocked: useLock,
        targetRef: creatorScrollReflike,
    });

    /**
     * Focus Creator so we can capture keyboard events. Otherwise Carousels and
     * FeatureCards will react to the keyboard.
     */
    useEffect(() => {
        if (isCreatorOpen) {
            creatorContentRef.current?.focus();
        }
    }, [isCreatorOpen, creatorScrollReflike]);

    /**
     * Set the body scroll lock to point to the Simplebar content wrapper.
     * This is required to get scrolling to work on IOS.
     */
    useEffect(() => {
        if (isCreatorOpen && isVisible) {
            const current = creatorSimpleBarRef?.current;

            // Typings for the Scrollable ref are not correct
            creatorScrollReflike.current = (current as any).contentWrapperEl;

            setUseLock(true);
        } else {
            setUseLock(false);
        }
    }, [isCreatorOpen, creatorScrollReflike, isVisible]);

    const handleApplyBpm = useCallback(
        (selectedBpm: number) => {
            dispatch({ type: 'changeBPM', payload: selectedBpm });
        },
        [dispatch],
    );

    // HANDLERS
    const handleToggle = (): void => {
        toggleCreator();
    };

    const handleClickPlay = (): void => {
        togglePause();
    };

    /**
     * Stop all keyboard events when creator is open
     */
    const handleKeyEvent = (e: React.KeyboardEvent): void => {
        if (isCreatorOpen) {
            e.nativeEvent.stopImmediatePropagation();
        }
    };

    const isLoading = !isInitialized || isSessionProcessing;
    const showTable = !isLoading && samplesCount !== 0;

    const classes = creator({ isSmall: samplesCount <= 1 });

    const getImageHeight = () => {
        return samplesCount > 1 ? `${samplesCount * 72 + 20 + 40}px` : '132px';
    };

    return (
        <Collapse
            isOpen={isCreatorOpen}
            className={classes.creator}
            aria-label={t`${CreatorBrand} Sample Player`}
            aria-busy={isLoading}
            hasTitleClick
            onToggle={handleToggle}
            padding="none"
            zIndex="absoluteTop"
        >
            <Collapse.Title
                width="100%"
                height={{ base: 'lg', md: 'xl' }}
                padding="none"
            >
                <CreatorHeader
                    onClickPlay={handleClickPlay}
                    onApplyBpm={handleApplyBpm}
                />
            </Collapse.Title>
            {/* NOTE: Keep empty trigger to prevent default trigger to be rendered */}
            <Collapse.Trigger />
            <Collapse.Content
                paddingTop="none"
                paddingBottom="xs"
                paddingX="xs"
            >
                <Scrollable
                    className={classes.scrollable}
                    ref={creatorSimpleBarRef}
                >
                    <Box
                        className={classes.content}
                        tabIndex={-1}
                        onKeyUpCapture={handleKeyEvent}
                        onKeyDownCapture={handleKeyEvent}
                        ref={creatorContentRef}
                    >
                        {showTable && (
                            <>
                                <Hidden at="md" operator=">">
                                    <SamplesCount samplesCount={samplesCount} />
                                </Hidden>
                                <Suspense fallback={null}>
                                    <MemoizedCreatorSamplesTable
                                        sampleIds={sampleIds}
                                    />
                                </Suspense>
                            </>
                        )}
                        {isLoading && (
                            <Box
                                className={classes.placeholder}
                                style={{ height: getImageHeight() }}
                                data-id="creator-loader"
                            >
                                <Progress />
                                <Text marginTop="md" fontWeight="bold">
                                    <Trans>Loading your beat ...</Trans>
                                </Text>
                            </Box>
                        )}
                        <CreatorFooter />
                    </Box>
                </Scrollable>
            </Collapse.Content>
        </Collapse>
    );
};

CreatorCollapse.displayName = 'CreatorCollapse';

export default withErrorBoundary(CreatorCollapse, 'empty');
